<template>
    <div>
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="activeSidebar"
                class="fixed inset-0 z-40"
                @click="UPDATE_SIDEBAR_STATE(false)"
            ></div>
        </transition>

        <div
            :class="[
                'transform fixed inset-y-0 left-0 z-50 w-64 max-w-full h-full flex flex-col bg-theme-card shadow-lg rounded-r-3xl transition-transform duration-300',
                activeSidebar ? 'translate-x-0' : '-translate-x-full'
            ]"
        >
            <div class="flex p-3">
                <div class="w-8/12 flex justify-end">
                    <router-link
                        to="/app"
                        rel="Home"
                        class="text-theme-primary underline cursor-pointer p-0"
                    >
                        <img
                            src="/img/logos/complete_logo_and_pet.png"
                            alt="Portal"
                            title="Logo completo"
                            loading="lazy"
                            height="60"
                            width="100"
                            class="max-60"
                        />
                    </router-link>
                </div>
                <div class="w-4/12 flex justify-end items-center">
                    <app-button
                        color="danger"
                        circle
                        icon
                        size="xl"
                        aria-label="Cerrar Sidebar"
                        @click="UPDATE_SIDEBAR_STATE(false)"
                    >
                        <img
                            src="@/assets/images/close.svg"
                            height="20"
                            width="20"
                            alt="close menu"
                        />
                    </app-button>
                </div>
            </div>

            <div class="flex-1 overflow-y-auto">
                <fragment
                    v-for="field in visibleSidebarItems"
                    :id="field.id || field.name"
                    :key="field.id || field.name"
                >
                    <div
                        v-if="
                            field.type === 'divider' &&
                            hasMerchantScope &&
                            selectedMerchant &&
                            selectedMerchant.merchantId
                        "
                        class="flex items-center px-3 py-2 my-1"
                    >
                        <div class="flex-1 border-t border-theme-border"></div>
                        <span
                            class="mx-2 text-xs text-theme-muted whitespace-nowrap"
                        >
                            🏪 {{ selectedMerchant.name }}
                        </span>
                        <div class="flex-1 border-t border-theme-border"></div>
                    </div>

                    <div
                        v-else-if="
                            field.type === 'divider' &&
                            hasMerchantScope &&
                            (!selectedMerchant || !selectedMerchant.merchantId)
                        "
                        class="px-4 py-3 my-1"
                    >
                        <div class="border-t border-theme-border mb-2"></div>
                        <p class="text-xs text-theme-muted text-center">
                            🏪 Selecciona una tienda para ver más opciones
                        </p>
                    </div>

                    <template
                        v-else-if="
                            field.merchantContext &&
                            (!selectedMerchant || !selectedMerchant.merchantId)
                        "
                    ></template>

                    <div v-else-if="field.locked">
                        <div
                            v-if="field.id === firstLockedId"
                            class="flex items-center px-3 py-2 mt-2"
                        >
                            <div
                                class="flex-1 border-t border-theme-border"
                            ></div>
                            <span
                                class="mx-2 text-xs text-theme-muted whitespace-nowrap"
                            >
                                Disponibles en otros planes
                            </span>
                            <div
                                class="flex-1 border-t border-theme-border"
                            ></div>
                        </div>
                        <button
                            type="button"
                            class="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-left text-theme-muted transition hover:bg-theme-hover"
                            @click="openLockedFeature(field)"
                        >
                            <span>{{ field.icon }}</span>
                            <span class="flex-1">{{ field.name }}</span>
                            <span class="text-xs">🔒</span>
                        </button>
                    </div>

                    <sidebar-group
                        v-else-if="field.type === 'group'"
                        :open="isGroupActive(field)"
                    >
                        <template #header>
                            <span>{{ field.icon }}</span>
                            <span>{{ field.name }}</span>
                        </template>
                        <sidebar-item
                            v-for="item in field.items"
                            :key="item.name"
                            :to="item.url"
                        >
                            <template #icon> 🔷 </template>
                            <span>{{ item.name }}</span>
                        </sidebar-item>
                    </sidebar-group>

                    <sidebar-item
                        v-else-if="field.type !== 'divider'"
                        :to="field.url"
                    >
                        <template #icon> {{ field.icon }} </template>
                        <span>{{ field.name }}</span>
                    </sidebar-item>
                </fragment>
            </div>

            <div class="flex flex-col p-3">
                <div
                    v-for="(item, index) in rolesComponent"
                    :key="index"
                    class="flex items-center my-1"
                >
                    <custom-avatar :badge="index === 0" badge-color="danger">
                        {{ item.emoji }}
                    </custom-avatar>
                    <strong class="ml-3 capitalize">
                        {{ item.name }}
                    </strong>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import { rolesEmoji } from "@/common/fields/roles";

import SidebarGroup from "@/components/layout/SidebarGroup.vue";
import SidebarItem from "@/components/layout/SidebarItem.vue";

export default {
    name: "SidebarApp",
    components: { SidebarItem, SidebarGroup },
    data: () => ({
        rolesComponent: []
    }),
    computed: {
        ...mapState("control", ["activeSidebar"]),
        ...mapGetters("control", ["visibleSidebarItems"]),
        ...mapState("auth", ["roles"]),
        ...mapState("commons", ["selectedMerchant"]),
        firstLockedId() {
            const field = this.visibleSidebarItems.find((item) => item.locked);
            return field ? field.id : null;
        },
        hasMerchantScope() {
            return this.$ability.hasPermission("merchant:ListOwn");
        }
    },
    mounted() {
        if (this.roles.length > 0) {
            this.rolesComponent = this.roles
                .filter((item) => !(item.slug || "").includes("basic"))
                .map((item) => ({
                    name: item.name,
                    emoji: rolesEmoji[item.slug] || "👷‍♂️"
                }));
        }
    },
    methods: {
        ...mapMutations("control", ["UPDATE_SIDEBAR_STATE"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openLockedFeature(field) {
            this.UPDATE_SIDEBAR_STATE(false);
            this.openPlanUpgrade({ kind: "feature", feature: field.feature });
        },
        isGroupActive(field) {
            return (field.items || []).some(
                (item) =>
                    this.$route.path === item.url ||
                    this.$route.path.startsWith(`${item.url}/`)
            );
        }
    }
};
</script>

<style scoped>
.max-60 {
    max-height: 50px !important;
}
</style>
